import {
  chartedFieldOf,
  ChartSpec,
  questionIdentity,
  specIdentity,
} from '@employ-engage/data';

/**
 * What is already on the dashboard, and how the card in front of you relates
 * to it.
 *
 * A chart is pinned when a chart *exactly like it* is pinned. The same
 * question drawn as a bar and as a pie are two charts and both are worth
 * keeping; a second identical bar is not. So the identity is the whole spec —
 * question, breakdown, measure, percentages, period and chart type — and the
 * pin icon follows the card as you change it.
 *
 * That leaves one way to be confused: change a pinned bar chart to a pie and
 * the icon empties, though you certainly pinned that question. Hence the
 * per-question count, which the card uses to say so.
 */

export type PinnedTile = { chartId: string };
export type PinnedChart = { id: string; spec: ChartSpec };

export type PinnedState = {
  /** Chart id, by the exact spec it was pinned with. */
  bySpec: Map<string, string>;
  /** How many pinned charts are about each question, however they are drawn. */
  countByQuestion: Map<string, number>;
};

export const pinnedState = (
  tiles: PinnedTile[],
  charts: PinnedChart[]
): PinnedState => {
  const specById = new Map(charts.map((chart) => [chart.id, chart.spec]));

  const bySpec = new Map<string, string>();
  const countByQuestion = new Map<string, number>();

  tiles.forEach((tile) => {
    const spec = specById.get(tile.chartId);
    // A tile whose chart has been deleted is nothing to match against.
    if (!spec) return;

    bySpec.set(specIdentity(spec), tile.chartId);

    const question = questionIdentity(chartedFieldOf(spec));
    if (question) {
      countByQuestion.set(question, (countByQuestion.get(question) ?? 0) + 1);
    }
  });

  return { bySpec, countByQuestion };
};

/** The chart pinned with exactly this configuration, if there is one. */
export const pinnedChartIdFor = (
  spec: ChartSpec | null | undefined,
  pinned: PinnedState
): string | null =>
  spec ? pinned.bySpec.get(specIdentity(spec)) ?? null : null;

/**
 * Other pinned views of the same question — those that are not this card as it
 * currently stands.
 */
export const otherPinnedViewsOf = (
  spec: ChartSpec | null | undefined,
  pinned: PinnedState
): number => {
  if (!spec) return 0;
  const question = questionIdentity(chartedFieldOf(spec));
  if (!question) return 0;

  const total = pinned.countByQuestion.get(question) ?? 0;
  return pinnedChartIdFor(spec, pinned) ? total - 1 : total;
};
